import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, Mic, Workflow, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const tools = [
    {
        icon: FileText,
        title: "Smart Resume Parser",
        description: "AI extracts skills, experience, education, and certifications with 99% accuracy. No manual data entry.",
        accent: "text-accent bg-accent/5 border-accent/20",
        features: ["Multi-format support (PDF, DOCX, LinkedIn)", "Structured skill taxonomy", "Auto-fill candidate profiles"],
        mockup: (
            <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-4 text-left">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Parsed</span>
                </div>
                <div className="space-y-2">
                    {[
                        { label: "Skills", value: "React, TypeScript, Node.js, AWS" },
                        { label: "Experience", value: "6 years • Senior Engineer" },
                        { label: "Match Score", value: "94%" },
                    ].map((item) => (
                        <div key={item.label} className="flex items-center justify-between text-xs">
                            <span className="text-slate-400 font-medium">{item.label}</span>
                            <span className="font-bold text-slate-700">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        ),
    },
    {
        icon: Mic,
        title: "Voice AI Screener",
        description: "Natural conversational AI that conducts 10-minute screening interviews, evaluating technical depth and soft skills in real-time.",
        accent: "text-blue-500 bg-blue-500/5 border-blue-500/20",
        features: ["Natural language understanding", "Adaptive follow-up questions", "Sentiment & confidence analysis"],
        mockup: (
            <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-4 text-left">
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Mic className="w-3 h-3 text-blue-500" />
                        </div>
                        <div className="rounded-xl rounded-tl-sm bg-blue-500/5 border border-blue-500/10 px-3 py-2">
                            <p className="text-xs text-slate-600">"Tell me about a time you optimized a database query for scale."</p>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <div className="rounded-xl rounded-tr-sm bg-slate-100 px-3 py-2 max-w-[80%]">
                            <p className="text-xs text-slate-600">"At ScaleApp, I reduced query time from 800ms to 45ms by..."</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full w-[85%] rounded-full bg-gradient-to-r from-blue-500 to-accent" />
                        </div>
                        <span className="text-[10px] font-bold text-blue-500">85% Technical</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        icon: Workflow,
        title: "Automation Engine",
        description: "Build IF/THEN workflows that auto-advance candidates, send notifications, and schedule interviews based on AI evaluation results.",
        accent: "text-amber-500 bg-amber-500/5 border-amber-500/20",
        features: ["Trigger-based pipelines", "Auto-schedule interviews", "Custom notification rules"],
        mockup: (
            <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-4 text-left">
                <div className="space-y-2">
                    {[
                        { condition: "IF", text: "AI Score ≥ 80%", color: "text-emerald-600 bg-emerald-50" },
                        { condition: "THEN", text: "Auto-advance to Round 2", color: "text-amber-600 bg-amber-50" },
                        { condition: "AND", text: "Notify hiring manager", color: "text-blue-600 bg-blue-50" },
                    ].map((rule) => (
                        <div key={rule.text} className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${rule.color}`}>{rule.condition}</span>
                            <span className="text-xs text-slate-600 font-medium">{rule.text}</span>
                        </div>
                    ))}
                </div>
            </div>
        ),
    },
];

const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const item = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ToolDemosSection = () => {
    return (
        <section className="py-32 bg-slate-50 relative overflow-hidden">
            <div className="absolute top-0 right-[10%] w-[500px] h-[500px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15),rgba(255,255,255,0)_70%)] rounded-full pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 text-accent text-xs font-bold uppercase tracking-wider mb-5 border border-accent/10"
                    >
                        Toolbox
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-display text-4xl md:text-6xl font-bold text-slate-900 mb-6"
                    >
                        All the Tools to <br /> <span className="text-accent italic">Hire Smarter</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-500 max-w-2xl mx-auto font-medium"
                    >
                        From parsing to screening to automation - every tool works together
                        to eliminate manual steps and surface the best candidates faster.
                    </motion.p>
                </div>

                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
                >
                    {tools.map((tool) => (
                        <motion.div
                            key={tool.title}
                            variants={item}
                            whileHover={{ y: -8 }}
                            className="group relative rounded-3xl p-8 bg-white border border-slate-100 hover:border-accent/20 shadow-sm hover:shadow-xl hover:shadow-accent/5 transition-all duration-300 overflow-hidden flex flex-col"
                        >
                            <div className={`w-14 h-14 rounded-2xl ${tool.accent} border flex items-center justify-center mb-6 shadow-sm`}>
                                <tool.icon className="w-7 h-7" />
                            </div>

                            <h3 className="font-display font-bold text-xl text-slate-900 mb-3">{tool.title}</h3>
                            <p className="text-slate-500 leading-relaxed text-sm font-medium mb-4">{tool.description}</p>

                            <ul className="space-y-2 mb-6">
                                {tool.features.map((f) => (
                                    <li key={f} className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                                        <CheckCircle className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            {tool.mockup}

                            <div className="mt-auto pt-6">
                                <Button variant="ghost" size="sm" asChild className="text-accent hover:text-white hover:bg-accent p-0 px-4 h-9 font-bold rounded-full border border-accent/20 hover:border-accent transition-all duration-200 group/btn">
                                    <Link to="/auth?mode=signup">
                                        Try it free
                                        <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                                    </Link>
                                </Button>
                            </div>

                            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-accent/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ToolDemosSection;
